import React from 'react';
import DodTermText from './DodTermText';
import { describeDetectedTarget, detectAgentDownloadTarget } from './agentDeviceDetect';
import Connect2AiPanel from './components/Connect2AiPanel';

type Checkin = {
  runtime?: string;
  cloud?: string;
  hostname?: string;
  label?: string;
  device_id?: string;
  reported_at?: string;
};

type AgentDevice = {
  device_id: string;
  hostname?: string;
  runtime?: string;
  cloud?: string;
  label?: string;
  reported_at?: string;
  token_id?: string;
  token_label?: string;
  token_prefix?: string;
};

type AgentToken = {
  token_id: string;
  label?: string;
  prefix?: string;
  active?: boolean;
  created_at?: string;
  last_used_at?: string;
  revoked_at?: string;
};

type AgentFinding = {
  finding_id: string;
  finding_key?: string;
  category?: string;
  severity?: string;
  title?: string;
  detail?: string;
  status?: string;
  device_id?: string;
  device_label?: string;
  hostname?: string;
  token_id?: string;
  token_label?: string;
  token_prefix?: string;
  occurrence_count?: number;
  evidence?: Array<{
    name?: string;
    path?: string;
    kind?: string;
    feature?: string;
    priority?: number;
  }>;
  open_console?: string;
  first_seen?: string;
  last_seen?: string;
  status_reason?: string;
};

type ScanRootOs = 'any' | 'windows' | 'macos' | 'linux';

type ScanRoot = {
  id?: string;
  path: string;
  os?: ScanRootOs | string;
  recursive?: boolean;
  preset?: boolean;
  label_en?: string;
  label_es?: string;
  hint_en?: string;
  hint_es?: string;
};

type CheckinCadence = {
  mode?: string;
  recommended?: string;
  checkin_cadence?: string;
  discovery_cadence?: string;
  note_en?: string;
  note_es?: string;
  delivery?: { mode?: string; note_en?: string; note_es?: string };
  checkin?: { cron?: string; interval_hours?: number; label_en?: string; label_es?: string };
  scan_roots?: ScanRoot[];
  scan_root_presets?: ScanRoot[];
  presets?: {
    checkin?: Array<{ id: string; label_en?: string; label_es?: string; cron?: string }>;
    discovery?: Array<{ id: string; label_en?: string; label_es?: string }>;
  };
};

type CheckinCadenceId = 'manual' | 'every_4h' | 'daily' | 'weekly';
type DiscoveryCadenceId = 'manual' | 'with_checkin' | 'every_4h' | 'daily' | 'weekly';

const DEFAULT_SCAN_ROOTS: ScanRoot[] = [
  { id: 'documents', path: '~/Documents', os: 'any', recursive: true, preset: true },
  { id: 'downloads', path: '~/Downloads', os: 'any', recursive: true, preset: true },
  { id: 'dropbox_home', path: '~/Dropbox', os: 'any', recursive: true, preset: true },
  { id: 'mac_cloudstorage', path: '~/Library/CloudStorage', os: 'macos', recursive: true, preset: true },
];

type DownloadTarget = {
  id: string;
  os: string;
  arch: string;
  filename: string;
  available?: boolean;
  url?: string;
  sha256?: string;
  kind?: string;
  signed?: boolean;
  note?: string;
  bytes?: number;
};

type InstallTabId = 'server' | 'windows' | 'docker' | 'kubernetes' | 'cloud';
type AwsRuntimeId = 'oneclick' | 'eks' | 'ecs' | 'ec2' | 'ssm' | 'azure' | 'gcp';
type AgentViewId = 'fleet' | 'install' | 'scope' | 'connect2ai';

type ScopePermissionMode = 'off' | 'on_approval' | 'auto';

type InstallInstructions = {
  security?: string[];
  server?: string[];
  windows?: string[];
  docker?: string[];
  kubernetes?: string[];
  cloud?: string[];
  fleet?: string[];
  package_managers?: string[];
  multi?: string[];
};

type PackageManagerChannel = {
  status?: string;
  formula_url?: string;
  manifest_zip_url?: string;
  install?: string[];
};

type PackageManagersInfo = {
  version?: string;
  homebrew?: PackageManagerChannel;
  winget?: PackageManagerChannel;
};

type Props = {
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
};

/** Daily-cadence agent: online within ~1.5 days; quiet through a week; then offline. */
const ONLINE_MS = 36 * 60 * 60 * 1000;
const STALE_MS = 7 * 24 * 60 * 60 * 1000;

type DeviceFreshness = 'online' | 'quiet' | 'offline' | 'unknown';

const deviceFreshness = (reportedAt?: string): DeviceFreshness => {
  if (!reportedAt) return 'unknown';
  const stamp = Date.parse(reportedAt);
  if (!Number.isFinite(stamp)) return 'unknown';
  const age = Date.now() - stamp;
  if (age <= ONLINE_MS) return 'online';
  if (age <= STALE_MS) return 'quiet';
  return 'offline';
};

const relativeWhen = (reportedAt: string | undefined, language: 'en' | 'es'): string => {
  if (!reportedAt) return language === 'es' ? 'Nunca' : 'Never';
  const stamp = Date.parse(reportedAt);
  if (!Number.isFinite(stamp)) return language === 'es' ? 'Nunca' : 'Never';
  const seconds = Math.max(0, Math.floor((Date.now() - stamp) / 1000));
  if (seconds < 60) return language === 'es' ? 'hace un momento' : 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return language === 'es' ? `hace ${minutes} min` : `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return language === 'es' ? `hace ${hours} h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return language === 'es' ? `hace ${days} d` : `${days}d ago`;
};

const runtimeLabel = (value: string | undefined, language: 'en' | 'es'): string => {
  if (value === 'cluster') return language === 'es' ? 'Clúster' : 'Cluster';
  if (value === 'container') return language === 'es' ? 'Contenedor' : 'Container';
  if (value === 'compute') return language === 'es' ? 'Servidor' : 'Server';
  return language === 'es' ? 'Aún no leído' : 'Not read yet';
};

const cloudLabel = (value: string | undefined, language: 'en' | 'es'): string => {
  if (value === 'aws') return 'Amazon Web Services';
  if (value === 'gcp') return 'Google Cloud';
  if (value === 'azure') return 'Microsoft Azure';
  if (value === 'unknown') return language === 'es' ? 'Nube no identificada' : 'Cloud not identified';
  return language === 'es' ? 'Sin lectura' : 'No reading yet';
};

const freshnessLabel = (value: DeviceFreshness, language: 'en' | 'es'): string => {
  if (value === 'online') return language === 'es' ? 'Al día' : 'Checking in';
  if (value === 'quiet') return language === 'es' ? 'En silencio' : 'Quiet';
  if (value === 'offline') return language === 'es' ? 'Sin señal' : 'Offline';
  return language === 'es' ? 'Sin datos' : 'No data';
};

const downloadLabel = (item: DownloadTarget): string => {
  if (item.kind === 'msi') return `Windows MSI ${item.arch}`;
  if (item.kind === 'pkg') return `macOS PKG ${item.arch}`;
  if (item.os === 'darwin') return `macOS ${item.arch}`;
  if (item.os === 'windows') return `Windows ${item.arch}`;
  return `Linux ${item.arch}`;
};

const osGroupLabel = (os: string, language: 'en' | 'es'): string => {
  if (os === 'darwin') return 'macOS';
  if (os === 'windows') return 'Windows';
  if (os === 'linux') return 'Linux';
  return language === 'es' ? 'Otro' : 'Other';
};

const groupDownloads = (items: DownloadTarget[]): Array<{ os: string; items: DownloadTarget[] }> => {
  const order = ['linux', 'darwin', 'windows'];
  const buckets = new Map<string, DownloadTarget[]>();
  for (const item of items) {
    const key = item.os || 'other';
    const list = buckets.get(key) || [];
    list.push(item);
    buckets.set(key, list);
  }
  const groups: Array<{ os: string; items: DownloadTarget[] }> = [];
  for (const os of order) {
    const list = buckets.get(os);
    if (list?.length) groups.push({ os, items: list });
  }
  for (const [os, list] of buckets) {
    if (!order.includes(os) && list.length) groups.push({ os, items: list });
  }
  return groups;
};

export default function AgentInstallPanel({ language, gatewayBaseUrl, accessToken }: Props): React.ReactElement {
  const [checkin, setCheckin] = React.useState<Checkin | null>(null);
  const [devices, setDevices] = React.useState<AgentDevice[]>([]);
  const [tokens, setTokens] = React.useState<AgentToken[]>([]);
  const [findings, setFindings] = React.useState<AgentFinding[]>([]);
  const [checkinCadence, setCheckinCadence] = React.useState<CheckinCadence | null>(null);
  const [findingsTokenFilter, setFindingsTokenFilter] = React.useState('');
  const [downloads, setDownloads] = React.useState<DownloadTarget[]>([]);
  const [fleetPackages, setFleetPackages] = React.useState<DownloadTarget[]>([]);
  const [checksumsUrl, setChecksumsUrl] = React.useState('');
  const [packageManagers, setPackageManagers] = React.useState<PackageManagersInfo>({});
  const [instructions, setInstructions] = React.useState<InstallInstructions>({});
  const [token, setToken] = React.useState('');
  const [tokenLabel, setTokenLabel] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [agentView, setAgentView] = React.useState<AgentViewId>(() => {
    try {
      const view = new URLSearchParams(window.location.search).get('view');
      if (view === 'install' || view === 'scope' || view === 'connect2ai' || view === 'fleet') {
        return view;
      }
    } catch {
      // ignore
    }
    return 'fleet';
  });
  const [installTab, setInstallTab] = React.useState<InstallTabId>('server');
  const [awsRuntime, setAwsRuntime] = React.useState<AwsRuntimeId>('oneclick');
  const [detectedDownloadId, setDetectedDownloadId] = React.useState('');
  const [detectNote, setDetectNote] = React.useState('');
  const [discoveryMode, setDiscoveryMode] = React.useState<ScopePermissionMode>('off');
  const [checkinCadenceId, setCheckinCadenceId] = React.useState<CheckinCadenceId>('every_4h');
  const [discoveryCadenceId, setDiscoveryCadenceId] = React.useState<DiscoveryCadenceId>('with_checkin');
  const [scanRoots, setScanRoots] = React.useState<ScanRoot[]>(DEFAULT_SCAN_ROOTS);
  const [scanRootPresets, setScanRootPresets] = React.useState<ScanRoot[]>([]);
  const [customRootPath, setCustomRootPath] = React.useState('');
  const [customRootOs, setCustomRootOs] = React.useState<ScanRootOs>('any');
  const [customRootRecursive, setCustomRootRecursive] = React.useState(true);
  const [previewSample, setPreviewSample] = React.useState('');
  const [previewHostOs, setPreviewHostOs] = React.useState<ScanRootOs>('macos');
  const [previewResult, setPreviewResult] = React.useState<string>('');
  const [hideRevokedTokens, setHideRevokedTokens] = React.useState(() => {
    try {
      return window.localStorage.getItem('nextcompli.agent.hideRevokedTokens') === '1';
    } catch {
      return false;
    }
  });
  const [scopeLoaded, setScopeLoaded] = React.useState(false);
  const installRef = React.useRef<HTMLElement | null>(null);
  const downloadListRef = React.useRef<HTMLDivElement | null>(null);
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const text = language === 'es'
    ? {
      title: 'Agente Monitor de NextCompli',
      eyebrow: 'Monitoreo continuo',
      lead: 'Cuatro áreas: flota, instalar, alcance de datos y Connect2AI (MCP para Cursor/Claude).',
      viewFleet: 'Flota',
      viewInstall: 'Instalar',
      viewScope: 'Alcance de datos',
      viewConnect2Ai: 'Connect2AI',
      goInstall: 'Ir a Instalar',
      scopeTitle: 'Alcance de datos',
      scopeLead: 'Controle qué puede observar el agente y enviar a NextCompli. Las puertas de acción (marcar pasos, archivar) siguen en cada función de cumplimiento.',
      scopeAlwaysTitle: 'Siempre en el check-in',
      scopeAlwaysBody: 'Nombre del equipo, dónde corre, nube detectada, etiqueta y id de dispositivo. Necesario para la flota. No incluye contenido de archivos.',
      scopeOptionalTitle: 'Opcional',
      scopeHintsTitle: 'Pistas de red para Vantage',
      scopeHintsBody: 'Dominio y lista de búsqueda de la red (nombres). Ayuda a la investigación de personas. Hoy viaja con el check-in.',
      scopeDiscoveryTitle: 'Descubrimiento de nombres (todas las funciones)',
      scopeDiscoveryBody: 'Solo nombres y rutas bajo las carpetas de la lista. NextCompli clasifica y prioriza por función (OFAC, nube, gobierno, sitio, IA, formación, investigación, etc.). Nunca abre el contenido. Requiere aprobación humana antes de pedir el escaneo.',
      scopeNeverTitle: 'Nunca se envía',
      scopeNeverBody: 'Contenido de archivos, correo, contraseñas, claves, historial del navegador o capturas de pantalla.',
      scopeModeOff: 'Apagado',
      scopeModeApproval: 'Tras aprobación',
      scopeSave: 'Guardar alcance',
      scopeSaving: 'Guardando…',
      scopeSaved: 'Alcance guardado.',
      scopeLoadError: 'No se pudo cargar el alcance.',
      scanRootsTitle: 'Carpetas que el agente puede listar',
      scanRootsLead: 'Lista de permitidos (estilo DLP/copia de seguridad). No todo el disco. El agente omite carpetas que no existan en ese equipo. Se aplica en el próximo check-in.',
      scanRootsPresets: 'Preajustes',
      scanRootsCustom: 'Ruta personalizada',
      scanRootsCustomHint: 'Ejemplos: D:\\Contracts, /Volumes/Shared/Vendors, %USERPROFILE%\\Finance. No C:\\ ni /.',
      scanRootsAdd: 'Añadir carpeta',
      scanRootsEmpty: 'Sin carpetas: el descubrimiento no hará cola hasta que añada al menos una.',
      scanRootsOs: 'Sistema',
      scanRootsOsAny: 'Cualquiera',
      scanRootsRecursive: 'Incluir subcarpetas',
      scanRootsThisFolder: 'Solo esta carpeta',
      scanRootsRemove: 'Quitar',
      scanRootsPreview: 'Probar patrón',
      scanRootsPreviewSample: 'Ruta de ejemplo en el equipo',
      scanRootsPreviewHost: 'SO del equipo',
      scanRootsPreviewRun: 'Comprobar',
      scanRootsPreviewMatch: 'Coincide con una carpeta permitida.',
      scanRootsPreviewMiss: 'No coincide. Ajuste la lista o la ruta de ejemplo.',
      scheduleTitle: 'Con qué frecuencia corre',
      scheduleLead: 'Predeterminado: check-in cada 4 horas y descubrimiento de nombres con cada check-in. Cámbielo abajo y pulse Guardar alcance. El agente obtiene el horario en el próximo check-in (no hay push).',
      scheduleCheckin: 'Check-in (flota)',
      scheduleDiscovery: 'Descubrimiento de nombres (flota)',
      scheduleCron: 'Cron sugerido',
      scheduleDelivery: 'Cómo llega al agente',
      scheduleDefaults: 'Usar valores predeterminados',
      scheduleFleetSummary: 'Frecuencia',
      scheduleChange: 'Cambiar en Alcance de datos',
      installTitle: 'Instalar',
      installLead: 'El escaneo público del sitio funciona sin agente. El agente (opcional) añade presencia en laptops, Docker o clústeres. Solo lectura: check-in HTTPS — sin archivos ni claves. Un comando por entorno.',
      create: 'Crear un token de instalación',
      creating: 'Creando…',
      tokenLabel: 'Nombre del sitio (opcional)',
      tokenLabelHint: 'Etiqueta para este token — por ejemplo el servidor o el clúster.',
      tokenLabelPlaceholder: 'ej. aws-prod o laptop-ops',
      tokenHelp: 'Copie este token ahora. NextCompli guarda solo el hash. Un token por cuenta de suscripción; puede reutilizarlo en muchos dispositivos o crear uno por sitio.',
      tokenStep: '1. Crear un token de instalación',
      tokenWhyTitle: 'Por qué necesita un token',
      tokenWhy: [
        'El token une el agente a esta cuenta de suscripción (esta empresa en NextCompli). Sin él, el programa no sabe a qué plan reportar.',
        'Se envía en cada check-in por HTTPS. No es la contraseña de inicio de sesión de la consola. Las puertas de cumplimiento (marcar pasos, escaneos) se configuran en cada función, no en este token.',
        'El SHA-256 / checksums.txt solo comprueba que el archivo descargado es el correcto. No sustituye al token y no registra el dispositivo.',
        'Cree el token mientras esté en esta suscripción. Si cambia de cuenta de pago, cree un token nuevo en esa cuenta.',
        'Para quitarlo de un equipo: revoque el token aquí, quite NEXTCOMPLI_AGENT_TOKEN del entorno o del MSI, y borre el binario. NextCompli solo guarda el hash.',
      ],
      downloadStep: '2. Descargar para su sistema',
      detectDevice: 'Detectar mi dispositivo',
      detectNotFound: 'No encontramos un binario para este navegador. Elija Linux, macOS o Windows abajo, o use la pestaña Nube para servidores en AWS, Google Cloud o Azure.',
      downloadChecksumNote: 'Opcional pero recomendado: verifique el SHA-256 con checksums.txt antes de ejecutar el archivo.',
      macGatekeeperTitle: 'macOS: si ve «No se abrió»',
      macGatekeeperBody: 'Las descargas Mac son binarios de laboratorio sin firmar. Gatekeeper de Apple las bloquea hasta que enviemos una versión notarizada. No las mueva a la Papelera.',
      macGatekeeperSteps: [
        'Pulse Listo (no Mover a la Papelera).',
        'En Finder: Control-clic en el archivo → Abrir → Abrir.',
        'Si sigue bloqueado: Ajustes del Sistema → Privacidad y seguridad → Abrir de todos modos.',
      ],
      macGatekeeperTerminal: 'xattr -dr com.apple.quarantine ~/Downloads/nextcompli-agent-darwin-* && chmod +x ~/Downloads/nextcompli-agent-darwin-*',
      macGatekeeperDownloaded: 'Descarga lista. Si macOS muestra No se abrió, pulse Listo (no Papelera), luego Control-clic → Abrir, o ejecute el comando xattr de abajo.',
      runStep: '3. Ejecutar la instalación',
      devicesTitle: 'Flota',
      tokensTitle: 'Tokens de instalación',
      tokensEmpty: 'Todavía no hay tokens. Cree uno para el primer dispositivo.',
      tokensHideRevoked: 'Ocultar tokens revocados',
      tokensHiddenCount: 'ocultos',
      tokenPrefixHint: 'Solo el prefijo de identificación. El secreto completo se muestra una vez al crear el token; NextCompli guarda el hash.',
      tokenPrefixLabel: 'prefijo',
      metricsLoading: 'Cargando estado de la flota',
      revoke: 'Revocar',
      forget: 'Quitar',
      downloadsTitle: 'Descargas',
      downloadMissing: 'Los binarios del agente aún no están en este gateway. En local, monte agents/nextcompli-agent/dist. En producción, el pipeline debe ejecutar prepare_gateway_agent_artifacts.sh al construir la imagen del gateway.',
      checksumsTitle: 'SHA-256',
      checksumsDownload: 'Descargar checksums.txt',
      fleetTitle: 'Paquetes de flota',
      fleetLead: 'MSI (Windows) y PKG (macOS) para MDM como Intune o Jamf. Los sin firmar son solo de laboratorio; la flota de clientes necesita certificados de firma.',
      fleetUnavailable: '',
      packageManagersTitle: 'Gestores de paquetes',
      packageHomebrew: 'Homebrew (macOS / Linux)',
      packageWinget: 'winget (Windows)',
      packageReady: 'Listo',
      packageUnavailable: 'No listo',
      packageCopy: 'Copiar comando',
      securityTitle: 'Comunicación segura',
      stepsTitle: 'Instrucciones de instalación',
      tabServer: 'Linux / macOS',
      tabWindows: 'Windows',
      tabDocker: 'Docker',
      tabK8s: 'Kubernetes',
      tabCloud: 'AWS',
      multiTitle: 'Varios dispositivos',
      copyHash: 'Copiar SHA-256',
      copyToken: 'Copiar token',
      ready: 'Listo',
      notReady: 'No listo',
      computer: 'Computadora',
      where: 'Dónde corre',
      cloud: 'Nube',
      when: 'Cuándo',
      active: 'Activo',
      revoked: 'Revocado',
      metricOnline: 'Al día',
      metricQuiet: 'En silencio',
      metricOffline: 'Sin señal',
      metricLastSeen: 'Último check-in',
      metricTokens: 'Tokens activos',
      metricFindings: 'Hallazgos abiertos',
      statusHealthy: 'Flota al día',
      statusQuiet: 'Flota en silencio',
      statusAttention: 'Hay dispositivos sin señal',
      statusIdle: 'Sin dispositivos',
      findingsTitle: 'Cola de hallazgos',
      findingsLead: 'Una cola para la empresa. Cada hallazgo indica el dispositivo y el token de instalación. Filtre por token si tiene varios sitios.',
      findingsEmpty: 'Sin hallazgos abiertos. Los check-in quietos u offline y el descubrimiento multi-función aparecen aquí.',
      findingsFilterAll: 'Todos los tokens',
      findingsFilterLabel: 'Filtrar por token',
      findingsCadenceFallback: 'El agente no corre en segundo plano. Solo reporta cuando alguien (o cron/MDM) ejecuta checkin. Apunte a una vez al día por dispositivo.',
      findingsAck: 'Reconocer',
      findingsResolve: 'Resolver',
      findingsDismiss: 'Descartar',
      findingsSeen: 'Visto',
      findingsToken: 'Token',
      findingsDevice: 'Dispositivo',
    }
    : {
      title: 'NextCompli Monitor Agent',
      eyebrow: 'Fleet monitoring',
      lead: 'Four areas: Fleet, Install, Data scope, and Connect2AI (MCP for Cursor/Claude).',
      viewFleet: 'Fleet',
      viewInstall: 'Install',
      viewScope: 'Data scope',
      viewConnect2Ai: 'Connect2AI',
      goInstall: 'Go to Install',
      scopeTitle: 'Data scope',
      scopeLead: 'Control what the agent may observe and send to NextCompli. Action gates (mark steps done, file online) stay on each compliance feature.',
      scopeAlwaysTitle: 'Always on check-in',
      scopeAlwaysBody: 'Computer name, where it runs, detected cloud, label, and device id. Required for the fleet. Does not include file contents.',
      scopeOptionalTitle: 'Optional',
      scopeHintsTitle: 'Network hints for Vantage',
      scopeHintsBody: 'Domain and network search list (names only). Helps person research. Travels with check-in today.',
      scopeDiscoveryTitle: 'Folder name discovery (all features)',
      scopeDiscoveryBody: 'Names and paths under the allowlisted folders only. NextCompli classifies and prioritizes by feature (OFAC, cloud, government, website, AI, formation, research, and more). Never opens file contents. Needs human approval before the scan is requested.',
      scopeNeverTitle: 'Never sent',
      scopeNeverBody: 'File contents, mail, passwords, keys, browser history, or screenshots.',
      scopeModeOff: 'Off',
      scopeModeApproval: 'On approval',
      scopeSave: 'Save scope',
      scopeSaving: 'Saving…',
      scopeSaved: 'Scope saved.',
      scopeLoadError: 'Could not load data scope.',
      scanRootsTitle: 'Folders the agent may list',
      scanRootsLead: 'Allowlist only (DLP / backup style) — never whole disk. The agent skips folders missing on that host. Applied on the next check-in.',
      scanRootsPresets: 'Presets',
      scanRootsCustom: 'Custom path',
      scanRootsCustomHint: 'Examples: D:\\Contracts, /Volumes/Shared/Vendors, %USERPROFILE%\\Finance. Not C:\\ or /.',
      scanRootsAdd: 'Add folder',
      scanRootsEmpty: 'No folders listed — discovery will not queue until you add at least one.',
      scanRootsOs: 'OS',
      scanRootsOsAny: 'Any',
      scanRootsRecursive: 'Include subfolders',
      scanRootsThisFolder: 'This folder only',
      scanRootsRemove: 'Remove',
      scanRootsPreview: 'Test pattern',
      scanRootsPreviewSample: 'Sample path on a host',
      scanRootsPreviewHost: 'Host OS',
      scanRootsPreviewRun: 'Check',
      scanRootsPreviewMatch: 'Matches an allowlisted folder.',
      scanRootsPreviewMiss: 'No match. Adjust the list or the sample path.',
      scheduleTitle: 'How often it runs',
      scheduleLead: 'Defaults: check-in every 4 hours and name discovery with each check-in. Change the menus and folders below, then click Save scope. The agent picks up the schedule on the next check-in (no push).',
      scheduleCheckin: 'Check-in (fleet)',
      scheduleDiscovery: 'Name discovery (fleet)',
      scheduleCron: 'Suggested cron',
      scheduleDelivery: 'How it reaches the agent',
      scheduleDefaults: 'Use defaults',
      scheduleFleetSummary: 'Frequency',
      scheduleChange: 'Change in Data scope',
      installTitle: 'Install',
      installLead: 'Public website scan works without an agent. The agent (optional) adds presence on laptops, Docker, or clusters. Read-only: HTTPS check-in — no files or keys. One command per environment.',
      create: 'Create an install token',
      creating: 'Creating…',
      tokenLabel: 'Site name (optional)',
      tokenLabelHint: 'A label for this token — for example the server or cluster name.',
      tokenLabelPlaceholder: 'e.g. aws-prod or laptop-ops',
      tokenHelp: 'Copy this token now. NextCompli stores only the hash. One token is enough per subscription account; reuse it on many devices, or create one per site to revoke a site alone.',
      tokenStep: '1. Create an install token',
      tokenWhyTitle: 'Why you need a token',
      tokenWhy: [
        'The token binds the agent to this subscription account (this company in NextCompli). Without it, the program has no plan to report to.',
        'It is sent on every check-in over HTTPS. It is not your Console login password. Compliance gates (step marks, folder discovery) are set on each feature, not on this token.',
        'SHA-256 / checksums.txt only proves the downloaded file is genuine. It does not replace the token and does not enroll the device.',
        'Create the token while you are signed into this subscription. If you switch paying accounts, create a new token under that account.',
        'To purge a machine: revoke the token here, remove NEXTCOMPLI_AGENT_TOKEN from the environment or MSI, and delete the binary. NextCompli stores only the hash.',
      ],
      downloadStep: '2. Download for your OS',
      detectDevice: 'Detect my device',
      detectNotFound: 'No matching binary for this browser. Pick Linux, macOS, or Windows below, or use the Cloud tab for AWS, Google Cloud, or Azure servers.',
      downloadChecksumNote: 'Optional but recommended: verify SHA-256 with checksums.txt before you run the file.',
      macGatekeeperTitle: 'macOS: if you see “Not Opened”',
      macGatekeeperBody: 'Mac downloads are unsigned lab binaries. Apple Gatekeeper blocks them until we ship a notarized build. Do not Move to Trash.',
      macGatekeeperSteps: [
        'Click Done (not Move to Trash).',
        'In Finder: Control-click the file → Open → Open.',
        'If it still blocks: System Settings → Privacy & Security → Open Anyway.',
      ],
      macGatekeeperTerminal: 'xattr -dr com.apple.quarantine ~/Downloads/nextcompli-agent-darwin-* && chmod +x ~/Downloads/nextcompli-agent-darwin-*',
      macGatekeeperDownloaded: 'Download ready. If macOS shows Not Opened, click Done (not Trash), then Control-click → Open, or run the xattr command below.',
      runStep: '3. Run the install',
      devicesTitle: 'Fleet',
      tokensTitle: 'Install tokens',
      tokensEmpty: 'No tokens yet. Create one for the first device.',
      tokensHideRevoked: 'Hide revoked tokens',
      tokensHiddenCount: 'hidden',
      tokenPrefixHint: 'ID prefix only. The full secret is shown once when you create the token; NextCompli stores the hash.',
      tokenPrefixLabel: 'prefix',
      metricsLoading: 'Loading fleet status',
      revoke: 'Revoke',
      forget: 'Remove',
      downloadsTitle: 'Downloads',
      downloadMissing: 'Agent binaries are not on this gateway yet. Locally, mount agents/nextcompli-agent/dist. In production, the pipeline must run prepare_gateway_agent_artifacts.sh when building the gateway image.',
      checksumsTitle: 'SHA-256',
      checksumsDownload: 'Download checksums.txt',
      fleetTitle: 'Fleet packages',
      fleetLead: 'MSI (Windows) and PKG (macOS) for MDM tools such as Intune or Jamf. Unsigned builds are lab-only; customer fleets need signing certificates.',
      fleetUnavailable: '',
      packageManagersTitle: 'Package managers',
      packageHomebrew: 'Homebrew (macOS / Linux)',
      packageWinget: 'winget (Windows)',
      packageReady: 'Ready',
      packageUnavailable: 'Not ready',
      packageCopy: 'Copy command',
      securityTitle: 'Secure communication',
      stepsTitle: 'Installation instructions',
      tabServer: 'Linux / macOS',
      tabWindows: 'Windows',
      tabDocker: 'Docker',
      tabK8s: 'Kubernetes',
      tabCloud: 'AWS',
      multiTitle: 'Many devices',
      copyHash: 'Copy SHA-256',
      copyToken: 'Copy token',
      ready: 'Ready',
      notReady: 'Not ready',
      computer: 'Computer',
      where: 'Where it runs',
      cloud: 'Cloud',
      when: 'When',
      active: 'Active',
      revoked: 'Revoked',
      metricOnline: 'Checking in',
      metricQuiet: 'Quiet',
      metricOffline: 'Offline',
      metricLastSeen: 'Last check-in',
      metricTokens: 'Active tokens',
      metricFindings: 'Open findings',
      statusHealthy: 'Fleet checking in',
      statusQuiet: 'Fleet quiet',
      statusAttention: 'Devices offline',
      statusIdle: 'No devices yet',
      findingsTitle: 'Findings queue',
      findingsLead: 'One company queue. Each finding shows the device and install token. Filter by token when you have several sites.',
      findingsEmpty: 'No open findings. Quiet/offline check-ins and multi-feature discovery hits show up here.',
      findingsFilterAll: 'All tokens',
      findingsFilterLabel: 'Filter by token',
      findingsCadenceFallback: 'The agent does not run in the background. It only reports when someone (or cron/MDM) runs checkin. Aim for about once a day per device.',
      findingsAck: 'Acknowledge',
      findingsResolve: 'Resolve',
      findingsDismiss: 'Dismiss',
      findingsSeen: 'Seen',
      findingsToken: 'Token',
      findingsDevice: 'Device',
    };

  const load = React.useCallback(async () => {
    if (!accessToken) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    try {
      const [monitorResponse, installResponse, scopeResponse] = await Promise.all([
        fetch(`${gateway}/api/v1/agent/monitor`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${gateway}/api/v1/agent/installations?language=${language}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${gateway}/api/v1/agent/monitor/permissions?feature=ofac-compliance`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);
      if (scopeResponse.ok) {
        const scopeBody = await scopeResponse.json() as { permissions?: Record<string, string> };
        const mode = String(scopeBody.permissions?.request_local_scans || 'off').toLowerCase();
        setDiscoveryMode(mode === 'on_approval' || mode === 'auto' ? 'on_approval' : 'off');
        setScopeLoaded(true);
      } else {
        setScopeLoaded(true);
      }
      if (monitorResponse.ok) {
        const body = await monitorResponse.json() as { checkin?: Checkin | null };
        setCheckin(body.checkin || null);
      }
      if (installResponse.ok) {
        const body = await installResponse.json() as {
          checkin?: Checkin | null;
          devices?: AgentDevice[];
          tokens?: AgentToken[];
          findings?: AgentFinding[];
          checkin_cadence?: CheckinCadence;
          downloads?: DownloadTarget[];
          fleet_packages?: DownloadTarget[];
          checksums_url?: string;
          package_managers?: PackageManagersInfo;
          instructions?: InstallInstructions;
        };
        setCheckin(body.checkin || null);
        setDevices(Array.isArray(body.devices) ? body.devices : []);
        setTokens(Array.isArray(body.tokens) ? body.tokens : []);
        setFindings(Array.isArray(body.findings) ? body.findings : []);
        const cadence = body.checkin_cadence && typeof body.checkin_cadence === 'object' ? body.checkin_cadence : null;
        setCheckinCadence(cadence);
        if (cadence?.checkin_cadence) {
          const id = String(cadence.checkin_cadence);
          if (id === 'manual' || id === 'every_4h' || id === 'daily' || id === 'weekly') {
            setCheckinCadenceId(id);
          }
        }
        if (cadence?.discovery_cadence) {
          const id = String(cadence.discovery_cadence);
          if (id === 'manual' || id === 'with_checkin' || id === 'every_4h' || id === 'daily' || id === 'weekly') {
            setDiscoveryCadenceId(id);
          }
        }
        if (Array.isArray(cadence?.scan_roots)) {
          setScanRoots(cadence.scan_roots.length ? cadence.scan_roots : []);
        }
        if (Array.isArray(cadence?.scan_root_presets)) {
          setScanRootPresets(cadence.scan_root_presets);
        }
        setDownloads(Array.isArray(body.downloads) ? body.downloads : []);
        setFleetPackages(Array.isArray(body.fleet_packages) ? body.fleet_packages : []);
        setChecksumsUrl(body.checksums_url || `${gateway}/api/v1/agent/download/checksums.txt`);
        setPackageManagers(
          body.package_managers && typeof body.package_managers === 'object'
            ? body.package_managers as PackageManagersInfo
            : {},
        );
        setInstructions(body.instructions || {});
      } else if (!monitorResponse.ok) {
        const status = await fetch(`${gateway}/api/v1/agent/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (status.ok) {
          const body = await status.json() as { checkin?: Checkin | null; devices?: AgentDevice[] };
          setCheckin(body.checkin || null);
          setDevices(Array.isArray(body.devices) ? body.devices : []);
        }
      }
    } catch {
      setError(language === 'es' ? 'No se pudo cargar el agente.' : 'Could not load the agent.');
    } finally {
      setLoaded(true);
    }
  }, [accessToken, gateway, language]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const createToken = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/install-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label: tokenLabel.trim() || undefined }),
      });
      const body = await response.json().catch(() => ({})) as {
        token?: string;
        detail?: string;
        downloads?: DownloadTarget[];
      };
      if (!response.ok || !body.token) {
        setError(typeof body.detail === 'string' ? body.detail : (language === 'es' ? 'No se pudo crear el token.' : 'Could not create the token.'));
        return;
      }
      setToken(body.token);
      if (Array.isArray(body.downloads)) {
        setDownloads(body.downloads);
      }
      setMessage(text.tokenHelp);
      await load();
    } catch {
      setError(language === 'es' ? 'No se pudo crear el token.' : 'Could not create the token.');
    } finally {
      setBusy(false);
    }
  };

  const revokeToken = async (tokenId: string) => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/install-token/${encodeURIComponent(tokenId)}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : (language === 'es' ? 'No se pudo revocar.' : 'Could not revoke.'));
        return;
      }
      setMessage(language === 'es' ? 'Token revocado. Las lecturas siguientes con ese token fallan.' : 'Token revoked. Later check-ins with that token fail.');
      await load();
    } catch {
      setError(language === 'es' ? 'No se pudo revocar.' : 'Could not revoke.');
    } finally {
      setBusy(false);
    }
  };

  const forgetDevice = async (deviceId: string) => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/devices/${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : (language === 'es' ? 'No se pudo quitar.' : 'Could not remove.'));
        return;
      }
      await load();
    } catch {
      setError(language === 'es' ? 'No se pudo quitar.' : 'Could not remove.');
    } finally {
      setBusy(false);
    }
  };

  const applyDefaultSchedule = () => {
    setCheckinCadenceId('every_4h');
    setDiscoveryCadenceId('with_checkin');
    setScanRoots(DEFAULT_SCAN_ROOTS);
  };

  const addScanRoot = (entry: ScanRoot) => {
    const path = String(entry.path || '').trim();
    if (!path) return;
    const os = (entry.os || 'any') as ScanRootOs;
    const recursive = entry.recursive !== false;
    setScanRoots((prev) => {
      if (prev.some((row) => row.path === path && (row.os || 'any') === os && (row.recursive !== false) === recursive)) {
        return prev;
      }
      return [...prev, {
        id: entry.id || `custom:${path}`,
        path,
        os,
        recursive,
        preset: Boolean(entry.preset),
      }];
    });
  };

  const removeScanRoot = (index: number) => {
    setScanRoots((prev) => prev.filter((_, i) => i !== index));
  };

  const runScanRootPreview = async () => {
    setPreviewResult('');
    setError('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/scan-roots/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scan_roots: scanRoots,
          sample_path: previewSample,
          host_os: previewHostOs,
        }),
      });
      const body = await response.json().catch(() => ({})) as {
        detail?: string;
        would_scan?: boolean | null;
        note_en?: string;
        note_es?: string;
      };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : text.scopeLoadError);
        return;
      }
      if (!previewSample.trim()) {
        setPreviewResult(language === 'es' ? (body.note_es || '') : (body.note_en || ''));
        return;
      }
      setPreviewResult(body.would_scan ? text.scanRootsPreviewMatch : text.scanRootsPreviewMiss);
    } catch {
      setError(text.scopeLoadError);
    }
  };

  const saveScope = async (overrides?: {
    checkinCadence?: CheckinCadenceId;
    discoveryCadence?: DiscoveryCadenceId;
    scanRoots?: ScanRoot[];
  }) => {
    const nextCheckin = overrides?.checkinCadence || checkinCadenceId;
    const nextDiscovery = overrides?.discoveryCadence || discoveryCadenceId;
    const nextRoots = overrides?.scanRoots || scanRoots;
    if (overrides?.checkinCadence) setCheckinCadenceId(overrides.checkinCadence);
    if (overrides?.discoveryCadence) setDiscoveryCadenceId(overrides.discoveryCadence);
    if (overrides?.scanRoots) setScanRoots(overrides.scanRoots);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const [permResponse, scheduleResponse] = await Promise.all([
        fetch(`${gateway}/api/v1/agent/monitor/permissions`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ permissions: { request_local_scans: discoveryMode } }),
        }),
        fetch(`${gateway}/api/v1/agent/schedule`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            checkin_cadence: nextCheckin,
            discovery_cadence: nextDiscovery,
            scan_roots: nextRoots.map((row) => ({
              path: row.path,
              os: row.os || 'any',
              recursive: row.recursive !== false,
              id: row.id,
            })),
          }),
        }),
      ]);
      if (!permResponse.ok || !scheduleResponse.ok) {
        const body = await (permResponse.ok ? scheduleResponse : permResponse).json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : text.scopeLoadError);
        return;
      }
      const scheduleBody = await scheduleResponse.json() as CheckinCadence;
      setCheckinCadence(scheduleBody);
      if (scheduleBody.checkin_cadence === 'manual' || scheduleBody.checkin_cadence === 'every_4h'
        || scheduleBody.checkin_cadence === 'daily' || scheduleBody.checkin_cadence === 'weekly') {
        setCheckinCadenceId(scheduleBody.checkin_cadence);
      }
      if (scheduleBody.discovery_cadence === 'manual' || scheduleBody.discovery_cadence === 'with_checkin'
        || scheduleBody.discovery_cadence === 'every_4h'
        || scheduleBody.discovery_cadence === 'daily' || scheduleBody.discovery_cadence === 'weekly') {
        setDiscoveryCadenceId(scheduleBody.discovery_cadence);
      }
      if (Array.isArray(scheduleBody.scan_roots)) {
        setScanRoots(scheduleBody.scan_roots);
      }
      if (Array.isArray(scheduleBody.scan_root_presets)) {
        setScanRootPresets(scheduleBody.scan_root_presets);
      }
      setMessage(text.scopeSaved);
    } catch {
      setError(text.scopeLoadError);
    } finally {
      setBusy(false);
    }
  };

  const resetScheduleDefaults = async () => {
    applyDefaultSchedule();
    await saveScope({
      checkinCadence: 'every_4h',
      discoveryCadence: 'with_checkin',
      scanRoots: DEFAULT_SCAN_ROOTS,
    });
  };

  const triageFinding = async (findingId: string, status: 'acknowledged' | 'resolved' | 'dismissed') => {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/findings/${encodeURIComponent(findingId)}/triage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : (language === 'es' ? 'No se pudo actualizar.' : 'Could not update.'));
        return;
      }
      await load();
    } catch {
      setError(language === 'es' ? 'No se pudo actualizar.' : 'Could not update.');
    } finally {
      setBusy(false);
    }
  };

  const downloadBinary = async (target: DownloadTarget) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/download/${encodeURIComponent(target.id)}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { detail?: string };
        setError(typeof body.detail === 'string' ? body.detail : text.downloadMissing);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = target.filename || `nextcompli-agent-${target.id}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      if (target.os === 'darwin' || target.id.startsWith('darwin-')) {
        setMessage(text.macGatekeeperDownloaded);
      }
    } catch {
      setError(text.downloadMissing);
    } finally {
      setBusy(false);
    }
  };

  const detectMyDevice = async () => {
    setError('');
    setDetectNote('');
    setBusy(true);
    try {
      const detected = await detectAgentDownloadTarget();
      if (!detected) {
        setDetectedDownloadId('');
        setDetectNote(text.detectNotFound);
        return;
      }
      const catalog = downloads.length ? downloads : [
        { id: 'linux-amd64', os: 'linux', arch: 'amd64', filename: 'nextcompli-agent-linux-amd64' },
        { id: 'linux-arm64', os: 'linux', arch: 'arm64', filename: 'nextcompli-agent-linux-arm64' },
        { id: 'darwin-amd64', os: 'darwin', arch: 'amd64', filename: 'nextcompli-agent-darwin-amd64' },
        { id: 'darwin-arm64', os: 'darwin', arch: 'arm64', filename: 'nextcompli-agent-darwin-arm64' },
        { id: 'windows-amd64', os: 'windows', arch: 'amd64', filename: 'nextcompli-agent-windows-amd64.exe' },
        { id: 'windows-arm64', os: 'windows', arch: 'arm64', filename: 'nextcompli-agent-windows-arm64.exe' },
      ];
      const match = catalog.find((item) => item.id === detected.id)
        || catalog.find((item) => item.os === detected.os && item.arch === detected.arch && !item.kind);
      if (!match) {
        setDetectedDownloadId('');
        setDetectNote(text.detectNotFound);
        return;
      }
      setDetectedDownloadId(match.id);
      const note = describeDetectedTarget(detected, language);
      setDetectNote(
        match.available === false
          ? `${note} ${language === 'es' ? 'Ese binario aún no está listo en este gateway.' : 'That binary is not ready on this gateway yet.'}`
          : `${note} ${language === 'es' ? 'Pulse el botón resaltado para descargar.' : 'Click the highlighted button to download.'}`,
      );
      downloadListRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    } finally {
      setBusy(false);
    }
  };


  const siteLabel = tokenLabel.trim() || 'site-1';
  const tokenValue = token || 'ncagent_…';
  const checkinLine = `nextcompli-agent checkin --gateway ${gateway} --token ${tokenValue} --label ${siteLabel}`;
  /** One-liner: download binary via public package path + check in (Linux/macOS). */
  const guidedCurlInstallLine = [
    `export NEXTCOMPLI_GATEWAY='${gateway}'`,
    `export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'`,
    `curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\`,
    `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh" | bash -s -- --checkin --label ${siteLabel}`,
  ].join('\n');
  const guidedBrewLine = packageManagers.homebrew?.status === 'ready' && packageManagers.homebrew.formula_url
    ? [
      `brew install --formula ${packageManagers.homebrew.formula_url}`,
      checkinLine,
    ].join('\n')
    : '';
  const guidedHelmLine = [
    '# Fetches the Helm chart from NextCompli (no git clone).',
    'TMP="$(mktemp -d)"',
    `curl -fsSL -H "Authorization: Bearer ${tokenValue}" \\`,
    `  "${gateway}/api/v1/agent/deploy/helm/chart.tgz" -o "$TMP/nextcompli-agent.tgz"`,
    'helm upgrade --install nextcompli-agent "$TMP/nextcompli-agent.tgz" \\',
    '  --namespace nextcompli-agent --create-namespace \\',
    `  --set gateway=${gateway} \\`,
    `  --set token=${tokenValue} \\`,
    `  --set label=${siteLabel} \\`,
    `  --set clusterId=${siteLabel} \\`,
    '  --set image.repository=YOUR_REGISTRY/nextcompli-agent \\',
    '  --set image.tag=VERSION',
  ].join('\n');
  const guidedDockerLine = [
    '# Image must already be in your registry (build agents/nextcompli-agent/Dockerfile once).',
    `docker run --rm \\`,
    `  -e NEXTCOMPLI_GATEWAY=${gateway} \\`,
    `  -e NEXTCOMPLI_AGENT_TOKEN=${tokenValue} \\`,
    `  -e NEXTCOMPLI_AGENT_LABEL=${siteLabel} \\`,
    `  -e NEXTCOMPLI_AGENT_CLUSTER_ID=${siteLabel} \\`,
    `  -e HTTPS_PROXY -e HTTP_PROXY -e NO_PROXY \\`,
    `  YOUR_REGISTRY/nextcompli-agent:VERSION run`,
  ].join('\n');
  const guidedEcsCfnLine = [
    '# Downloads the CloudFormation template from NextCompli, then deploys with AWS CLI.',
    `curl -fsSL -H "Authorization: Bearer ${tokenValue}" \\`,
    `  "${gateway}/api/v1/agent/deploy/aws/cloudformation-ecs.yaml" -o /tmp/nextcompli-agent-ecs.yaml`,
    'aws cloudformation deploy \\',
    '  --stack-name nextcompli-agent \\',
    '  --template-file /tmp/nextcompli-agent-ecs.yaml \\',
    '  --capabilities CAPABILITY_NAMED_IAM \\',
    '  --parameter-overrides \\',
    '    ClusterName=YOUR_ECS_CLUSTER \\',
    `    GatewayUrl=${gateway} \\`,
    `    AgentToken=${tokenValue} \\`,
    `    SiteLabel=${siteLabel} \\`,
    '    ImageUri=YOUR_ACCOUNT.dkr.ecr.REGION.amazonaws.com/nextcompli-agent:VERSION \\',
    '    SubnetIds=subnet-aaa\\\\,subnet-bbb \\',
    '    SecurityGroupIds=sg-xxx',
  ].join('\n');
  const guidedEc2UserDataLine = [
    `# EC2 Launch Template user-data — script fetched from the gateway.`,
    `export NEXTCOMPLI_GATEWAY='${gateway}'`,
    `export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'`,
    `export NEXTCOMPLI_AGENT_LABEL='${siteLabel}'`,
    `curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\`,
    `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/deploy/aws/ec2-user-data.sh" | sudo -E bash`,
  ].join('\n');
  const guidedAwsOneclickLine = [
    `export NEXTCOMPLI_GATEWAY='${gateway}'`,
    `export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'`,
    `export NEXTCOMPLI_AGENT_LABEL='${siteLabel}'`,
    'curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\',
    `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install-aws-oneclick.sh" | bash -s -- --label ${siteLabel} --yes`,
  ].join('\n');
  const guidedAwsSsmLine = [
    `export NEXTCOMPLI_GATEWAY='${gateway}'`,
    `export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'`,
    `export NEXTCOMPLI_AGENT_LABEL='${siteLabel}'`,
    'curl -fsSL -H "Authorization: Bearer $NEXTCOMPLI_AGENT_TOKEN" \\',
    `  "$NEXTCOMPLI_GATEWAY/api/v1/agent/install-aws-ssm.sh" | bash -s -- --checkin --label ${siteLabel}`,
  ].join('\n');
  const guidedAzureLine = [
    'RG=YOUR_RESOURCE_GROUP',
    'VM=YOUR_VM_NAME',
    'az vm run-command invoke --resource-group "$RG" --name "$VM" --command-id RunShellScript --scripts \\',
    `  "export NEXTCOMPLI_GATEWAY='${gateway}'; export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'; curl -fsSL -H \\"Authorization: Bearer \\$NEXTCOMPLI_AGENT_TOKEN\\" \\"\\$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh\\" | bash -s -- --checkin --label ${siteLabel}"`,
  ].join('\n');
  const guidedGcpLine = [
    'ZONE=us-central1-a',
    'INSTANCE=YOUR_INSTANCE',
    'gcloud compute ssh "$INSTANCE" --zone="$ZONE" --command \\',
    `  "export NEXTCOMPLI_GATEWAY='${gateway}'; export NEXTCOMPLI_AGENT_TOKEN='${tokenValue}'; curl -fsSL -H 'Authorization: Bearer \\$NEXTCOMPLI_AGENT_TOKEN' '\\$NEXTCOMPLI_GATEWAY/api/v1/agent/install.sh' | bash -s -- --checkin --label ${siteLabel}"`,
  ].join('\n');
  const guidedCommandForTab = (): string => {
    if (installTab === 'kubernetes') return guidedHelmLine;
    if (installTab === 'docker') return guidedDockerLine;
    if (installTab === 'cloud') {
      if (awsRuntime === 'oneclick') return guidedAwsOneclickLine;
      if (awsRuntime === 'eks') return guidedHelmLine;
      if (awsRuntime === 'ecs') return guidedEcsCfnLine;
      if (awsRuntime === 'ssm') return guidedAwsSsmLine;
      if (awsRuntime === 'azure') return guidedAzureLine;
      if (awsRuntime === 'gcp') return guidedGcpLine;
      return guidedEc2UserDataLine;
    }
    // Servers: curl|bash one-liner (no apt repo yet).
    if (installTab === 'server') return guidedCurlInstallLine;
    return checkinLine;
  };
  const windowsOneLiner = token
    ? `$env:NEXTCOMPLI_GATEWAY='${gateway}'; $env:NEXTCOMPLI_AGENT_TOKEN='${token}'; irm "$env:NEXTCOMPLI_GATEWAY/api/v1/agent/install.ps1" -Headers @{ Authorization = "Bearer $env:NEXTCOMPLI_AGENT_TOKEN" } | iex`
    : `$env:NEXTCOMPLI_GATEWAY='${gateway}'; $env:NEXTCOMPLI_AGENT_TOKEN='ncagent_…'; irm "$env:NEXTCOMPLI_GATEWAY/api/v1/agent/install.ps1" -Headers @{ Authorization = "Bearer $env:NEXTCOMPLI_AGENT_TOKEN" } | iex`;
  const fallbackSteps: Record<InstallTabId, string[]> = language === 'es'
    ? {
      server: [
        'Descargue el binario Linux o macOS (amd64 o arm64) arriba.',
        'macOS: si aparece No se abrió, pulse Listo (no Papelera). Control-clic → Abrir → Abrir, o: xattr -dr com.apple.quarantine ~/Downloads/nextcompli-agent-darwin-* && chmod +x ~/Downloads/nextcompli-agent-darwin-*',
        'chmod +x nextcompli-agent && sudo mv nextcompli-agent /usr/local/bin/',
        'Opcional: shasum -a 256 nextcompli-agent y compare con checksums.txt',
        checkinLine,
      ],
      windows: [
        'En PowerShell, cree el token arriba y luego ejecute:',
        windowsOneLiner,
        'O descargue el .exe, verifique el SHA-256, y ejecute checkin con --token.',
      ],
      docker: [
        'Construya agents/nextcompli-agent/Dockerfile. No meta el token en la imagen.',
        `docker run --rm -e NEXTCOMPLI_GATEWAY=${gateway} -e NEXTCOMPLI_AGENT_TOKEN=${token || 'ncagent_…'} -e NEXTCOMPLI_AGENT_LABEL=docker-1 -e NEXTCOMPLI_AGENT_CLUSTER_ID=docker-1 nextcompli-agent run`,
        'Un solo check-in: use el comando checkin en lugar de run.',
      ],
      kubernetes: [
        'Un comando (Helm). El agente es de solo lectura — solo check-in HTTPS.',
        `helm upgrade --install nextcompli-agent ./agents/nextcompli-agent/deploy/helm/nextcompli-agent --namespace nextcompli-agent --create-namespace --set gateway=${gateway} --set token=${token || 'ncagent_…'} --set label=${tokenLabel.trim() || 'jfgoss-prod'} --set clusterId=${tokenLabel.trim() || 'jfgoss-prod'} --set image.repository=YOUR_ECR/nextcompli-agent --set image.tag=VERSION`,
        'Presencia continua: agregue --set mode=deployment',
        'Sin Helm: manifiestos en deploy/kubernetes. Guarde el token en un Secret, no en Git.',
      ],
      cloud: [
        'Empiece por AWS (un clic): detecta SSM, EKS o provisiona un EC2. Otras pestañas: SSM solo, EC2, ECS, EKS, Azure, gcloud.',
        'Tras el check-in el agente reporta cloud + runtime + hostname. No adivinamos el entorno desde la URL del sitio.',
        'Cortafuegos: HTTPS 443 saliente al gateway; proxy = HTTPS_PROXY. Sin entrada.',
        'Solo lectura · el token no va en la AMI ni en Git.',
      ],
    }
    : {
      server: [
        'Download the Linux or macOS binary (amd64 or arm64) above.',
        'macOS: if you see Not Opened, click Done (not Trash). Control-click → Open → Open, or: xattr -dr com.apple.quarantine ~/Downloads/nextcompli-agent-darwin-* && chmod +x ~/Downloads/nextcompli-agent-darwin-*',
        'chmod +x nextcompli-agent && sudo mv nextcompli-agent /usr/local/bin/',
        'Optional: shasum -a 256 nextcompli-agent and compare to checksums.txt',
        checkinLine,
      ],
      windows: [
        'In PowerShell, create the token above, then run:',
        windowsOneLiner,
        'Or download the .exe, verify SHA-256, and run checkin with --token.',
      ],
      docker: [
        'Build agents/nextcompli-agent/Dockerfile. Do not bake the token into the image.',
        `docker run --rm -e NEXTCOMPLI_GATEWAY=${gateway} -e NEXTCOMPLI_AGENT_TOKEN=${token || 'ncagent_…'} -e NEXTCOMPLI_AGENT_LABEL=docker-1 -e NEXTCOMPLI_AGENT_CLUSTER_ID=docker-1 nextcompli-agent run`,
        'One-shot check-in: use the checkin command instead of run.',
      ],
      kubernetes: [
        'One command (Helm). The agent is read-only — HTTPS check-in only.',
        `helm upgrade --install nextcompli-agent ./agents/nextcompli-agent/deploy/helm/nextcompli-agent --namespace nextcompli-agent --create-namespace --set gateway=${gateway} --set token=${token || 'ncagent_…'} --set label=${tokenLabel.trim() || 'jfgoss-prod'} --set clusterId=${tokenLabel.trim() || 'jfgoss-prod'} --set image.repository=YOUR_ECR/nextcompli-agent --set image.tag=VERSION`,
        'Always-on presence: add --set mode=deployment',
        'Without Helm: manifests in deploy/kubernetes. Keep the token in a Secret, not in git.',
      ],
      cloud: [
        'Start with AWS (one-click): detects SSM, EKS, or provisions an EC2. Other tabs: SSM-only, EC2, ECS, EKS, Azure, gcloud.',
        'After check-in the agent reports cloud + runtime + hostname. We do not guess the environment from the website URL.',
        'Firewall: outbound HTTPS 443 to the gateway; proxy = HTTPS_PROXY. No inbound ports.',
        'Read-only · never bake the token into an AMI or git.',
      ],
    };
  const stepList = (instructions[installTab] && instructions[installTab]!.length)
    ? instructions[installTab]!
    : fallbackSteps[installTab];
  const multiSteps = (instructions.multi && instructions.multi.length)
    ? instructions.multi
    : (language === 'es'
      ? [
        'Repita la descarga y el checkin en cada servidor, laptop o clúster.',
        'Cada lectura aparece como un dispositivo separado.',
        'Un token activo puede servir a muchos dispositivos hasta que lo revoque.',
      ]
      : [
        'Repeat the download and checkin on each server, laptop, or cluster.',
        'Each check-in appears as a separate connected device.',
        'One active token can cover many devices until you revoke it.',
      ]);
  const seenTerms = new Set<string>();
  const fallbackDownloads: DownloadTarget[] = [
    { id: 'linux-amd64', os: 'linux', arch: 'amd64', filename: 'nextcompli-agent-linux-amd64' },
    { id: 'linux-arm64', os: 'linux', arch: 'arm64', filename: 'nextcompli-agent-linux-arm64' },
    { id: 'darwin-amd64', os: 'darwin', arch: 'amd64', filename: 'nextcompli-agent-darwin-amd64' },
    { id: 'darwin-arm64', os: 'darwin', arch: 'arm64', filename: 'nextcompli-agent-darwin-arm64' },
    { id: 'windows-amd64', os: 'windows', arch: 'amd64', filename: 'nextcompli-agent-windows-amd64.exe' },
    { id: 'windows-arm64', os: 'windows', arch: 'arm64', filename: 'nextcompli-agent-windows-arm64.exe' },
  ];
  const downloadItems = downloads.length ? downloads : fallbackDownloads;
  const downloadsMissing = downloadItems.length > 0
    && downloadItems.every((item) => item.available === false);
  const downloadGroups = groupDownloads(downloadItems);
  const showMacGatekeeper = downloadItems.some((item) => item.os === 'darwin')
    || detectedDownloadId.startsWith('darwin-');
  const activeTokenCount = tokens.filter((row) => row.active !== false && !row.revoked_at).length;
  const revokedTokenCount = tokens.length - activeTokenCount;
  const visibleTokens = hideRevokedTokens
    ? tokens.filter((row) => row.active !== false && !row.revoked_at)
    : tokens;
  React.useEffect(() => {
    try {
      window.localStorage.setItem('nextcompli.agent.hideRevokedTokens', hideRevokedTokens ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [hideRevokedTokens]);
  React.useEffect(() => {
    if (!hideRevokedTokens || !findingsTokenFilter) return;
    const selected = tokens.find((row) => row.token_id === findingsTokenFilter);
    if (selected && (selected.active === false || selected.revoked_at)) {
      setFindingsTokenFilter('');
    }
  }, [hideRevokedTokens, findingsTokenFilter, tokens]);
  const freshnessCounts = devices.reduce(
    (acc, device) => {
      const state = deviceFreshness(device.reported_at);
      if (state === 'online') acc.online += 1;
      else if (state === 'quiet') acc.quiet += 1;
      else if (state === 'offline') acc.offline += 1;
      return acc;
    },
    { online: 0, quiet: 0, offline: 0 },
  );
  const lastSeenAt = devices.reduce<string | undefined>((latest, device) => {
    const stamp = device.reported_at;
    if (!stamp) return latest;
    if (!latest) return stamp;
    return Date.parse(stamp) > Date.parse(latest) ? stamp : latest;
  }, checkin?.reported_at);
  const fleetTone = !devices.length
    ? 'idle'
    : freshnessCounts.offline > 0
      ? 'attention'
      : freshnessCounts.online > 0
        ? 'healthy'
        : 'idle';
  const fleetStatusLabel = !devices.length
    ? text.statusIdle
    : freshnessCounts.offline > 0
      ? text.statusAttention
      : freshnessCounts.online > 0
        ? text.statusHealthy
        : text.statusQuiet;
  const openFindings = findings.filter((item) => {
    const status = String(item.status || 'open');
    return status === 'open' || status === 'acknowledged' || status === 'investigating';
  });
  const visibleFindings = findingsTokenFilter
    ? openFindings.filter((item) => item.token_id === findingsTokenFilter)
    : openFindings;
  const cadenceNote = language === 'es'
    ? (checkinCadence?.note_es || text.findingsCadenceFallback)
    : (checkinCadence?.note_en || text.findingsCadenceFallback);
  const checkinFrequencyLabel = (
    (checkinCadence?.presets?.checkin || []).find((row) => row.id === checkinCadenceId)
  );
  const discoveryFrequencyLabel = (
    (checkinCadence?.presets?.discovery || []).find((row) => row.id === discoveryCadenceId)
  );
  const frequencySummary = language === 'es'
    ? `${checkinFrequencyLabel?.label_es || checkinFrequencyLabel?.label_en || checkinCadenceId} · descubrimiento ${discoveryFrequencyLabel?.label_es || discoveryFrequencyLabel?.label_en || discoveryCadenceId}`
    : `${checkinFrequencyLabel?.label_en || checkinCadenceId} · discovery ${discoveryFrequencyLabel?.label_en || discoveryCadenceId}`;

  const copyText = async (value: string, okMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(okMessage);
    } catch {
      setError(language === 'es' ? 'No se pudo copiar.' : 'Could not copy.');
    }
  };

  const copySha = async (value: string) => {
    await copyText(value, language === 'es' ? 'SHA-256 copiado.' : 'SHA-256 copied.');
  };

  return (
    <section className="card settings-card agent-install" aria-label={text.title}>
      <header className="agent-install__hero">
        <div className="agent-install__hero-copy">
          <p className="agent-install__eyebrow">{text.eyebrow}</p>
          <h2>{text.title}</h2>
          <p className="agent-install__lead">
            <DodTermText text={text.lead} language={language} seen={seenTerms} />
          </p>
        </div>
        <div className={`agent-install__status-pill is-${fleetTone}`} role="status">
          <span className="agent-install__status-dot" aria-hidden="true" />
          {fleetStatusLabel}
        </div>
      </header>

      <div className="agent-install__views" role="tablist" aria-label={text.title}>
        {([
          { id: 'fleet' as const, label: text.viewFleet, badge: openFindings.length || undefined },
          { id: 'install' as const, label: text.viewInstall },
          { id: 'scope' as const, label: text.viewScope },
          { id: 'connect2ai' as const, label: text.viewConnect2Ai },
        ]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={agentView === tab.id}
            className={agentView === tab.id ? 'is-active' : undefined}
            onClick={() => setAgentView(tab.id)}
          >
            {tab.label}
            {tab.badge ? <span className="agent-install__view-badge">{tab.badge}</span> : null}
          </button>
        ))}
      </div>

      {agentView === 'fleet' ? (
      <div className="agent-install__view-panel" role="tabpanel" aria-label={text.viewFleet}>
      <div
        className="agent-install__metrics"
        aria-label={language === 'es' ? 'Estado de la flota' : 'Fleet status'}
        aria-busy={!loaded}
      >
        {([
          {
            key: 'online',
            value: freshnessCounts.online,
            label: text.metricOnline,
            tone: freshnessCounts.online ? 'is-success' : '',
          },
          {
            key: 'quiet',
            value: freshnessCounts.quiet,
            label: text.metricQuiet,
            tone: freshnessCounts.quiet ? 'is-attention' : '',
          },
          {
            key: 'offline',
            value: freshnessCounts.offline,
            label: text.metricOffline,
            tone: freshnessCounts.offline ? 'is-attention' : '',
          },
          {
            key: 'last',
            value: relativeWhen(lastSeenAt, language),
            label: text.metricLastSeen,
            tone: 'agent-install__metric--text',
          },
          {
            key: 'tokens',
            value: activeTokenCount,
            label: text.metricTokens,
            tone: '',
          },
          {
            key: 'findings',
            value: openFindings.length,
            label: text.metricFindings,
            tone: openFindings.length ? 'is-attention' : '',
          },
        ] as const).map((metric) => (
          <div
            key={metric.key}
            className={`agent-install__metric ${metric.tone} ${loaded ? '' : 'is-loading'}`.trim()}
          >
            {loaded ? (
              <em>{metric.value}</em>
            ) : (
              <span
                className="agent-install__metric-spinner"
                role="status"
                aria-label={text.metricsLoading}
              />
            )}
            <span>{metric.label}</span>
          </div>
        ))}
      </div>

      <p className="agent-install__cadence" role="note">{cadenceNote}</p>
      <p className="agent-install__frequency-summary">
        <span>
          {text.scheduleFleetSummary}: {frequencySummary}
          {checkinCadenceId === 'daily' && discoveryCadenceId === 'with_checkin'
            ? ` (${language === 'es' ? 'predeterminado' : 'default'})`
            : ''}
        </span>
        <button type="button" className="dashboard-security-inline-btn" onClick={() => setAgentView('scope')}>
          {text.scheduleChange}
        </button>
      </p>

      <details className="agent-install__details agent-install__section" open={visibleFindings.length > 0}>
        <summary>
          <h3 id="agent-findings-heading">{text.findingsTitle}</h3>
          <span className={`agent-install__badge ${openFindings.length ? 'is-attention' : 'is-success'}`}>
            {openFindings.length}
          </span>
        </summary>
        <p className="agent-install__hint">
          <DodTermText text={text.findingsLead} language={language} seen={new Set()} />
        </p>
        {visibleTokens.length > 1 ? (
          <label className="agent-install__findings-filter">
            <span>{text.findingsFilterLabel}</span>
            <select
              value={findingsTokenFilter}
              onChange={(event) => setFindingsTokenFilter(event.target.value)}
              aria-label={text.findingsFilterLabel}
            >
              <option value="">{text.findingsFilterAll}</option>
              {visibleTokens.map((item) => (
                <option key={item.token_id} value={item.token_id}>
                  {item.label || item.prefix || item.token_id}
                  {item.prefix ? ` (${item.prefix}…)` : ''}
                  {!item.active || item.revoked_at ? ` · ${text.revoked}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {visibleFindings.length ? (
          <ul className="agent-install__queue">
            {visibleFindings.map((finding) => (
              <li key={finding.finding_id}>
                <div className="agent-install__device-main">
                  <strong>{finding.title || finding.finding_id}</strong>
                  <span>
                    {(finding.severity || 'medium').toUpperCase()}
                    {' · '}
                    {finding.status || 'open'}
                    {finding.occurrence_count && finding.occurrence_count > 1
                      ? ` · ×${finding.occurrence_count}`
                      : ''}
                    {' · '}
                    {text.findingsSeen} {relativeWhen(finding.last_seen, language)}
                  </span>
                </div>
                {finding.detail ? <p>{finding.detail}</p> : null}
                <p className="agent-install__finding-meta">
                  {text.findingsDevice}: {finding.device_label || finding.hostname || finding.device_id || '—'}
                  {' · '}
                  {text.findingsToken}:{' '}
                  {finding.token_label || finding.token_prefix || finding.token_id || text.findingsFilterAll}
                  {finding.token_prefix ? ` (${finding.token_prefix}…)` : ''}
                </p>
                {finding.evidence && finding.evidence.length ? (
                  <ul className="agent-install__finding-evidence">
                    {finding.evidence.slice(0, 8).map((item, index) => (
                      <li key={`${finding.finding_id}-ev-${index}`}>
                        {item.feature && item.feature !== 'unclassified' ? (
                          <span className="agent-install__evidence-feature">{item.feature}</span>
                        ) : null}
                        <code>{item.name || item.path || '—'}</code>
                        {item.path && item.name ? <span className="agent-install__evidence-path">{item.path}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {finding.open_console ? (
                  <p className="agent-install__finding-meta">
                    <a href={finding.open_console}>{finding.open_console}</a>
                  </p>
                ) : null}
                <div className="agent-install__actions">
                  {finding.status === 'open' ? (
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={busy}
                      onClick={() => void triageFinding(finding.finding_id, 'acknowledged')}
                    >
                      {text.findingsAck}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="dashboard-btn"
                    disabled={busy}
                    onClick={() => void triageFinding(finding.finding_id, 'resolved')}
                  >
                    {text.findingsResolve}
                  </button>
                  <button
                    type="button"
                    className="dashboard-btn"
                    disabled={busy}
                    onClick={() => void triageFinding(finding.finding_id, 'dismissed')}
                  >
                    {text.findingsDismiss}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="agent-install__hint">{text.findingsEmpty}</p>
        )}
      </details>

      {devices.length ? (
        <details className="agent-install__details agent-install__section">
          <summary>
            <h3 id="agent-devices-heading">{text.devicesTitle}</h3>
            <span className="agent-install__badge is-success">{devices.length}</span>
          </summary>
          <ul className="agent-install__devices">
            {devices.map((device) => {
              const fresh = deviceFreshness(device.reported_at);
              return (
                <li key={device.device_id}>
                  <div className="agent-install__device-main">
                    <strong>{device.label || device.hostname || device.device_id}</strong>
                    <span>
                      <span className={`agent-install__fresh is-${fresh}`}>{freshnessLabel(fresh, language)}</span>
                      {' · '}
                      {runtimeLabel(device.runtime, language)} · {cloudLabel(device.cloud, language)}
                      {' · '}
                      {relativeWhen(device.reported_at, language)}
                      {device.token_label || device.token_prefix
                        ? ` · ${text.findingsToken} ${device.token_label || device.token_prefix}${device.token_prefix ? ` (${device.token_prefix}…)` : ''}`
                        : ''}
                    </span>
                  </div>
                  <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void forgetDevice(device.device_id)}>
                    {text.forget}
                  </button>
                </li>
              );
            })}
          </ul>
        </details>
      ) : (
        <p className="agent-install__empty-strip">
          <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => setAgentView('install')}>
            {text.goInstall}
          </button>
        </p>
      )}
      </div>
      ) : null}

      {agentView === 'install' ? (
      <div className="agent-install__view-panel agent-install__sections" ref={installRef} role="tabpanel" aria-label={text.viewInstall}>
        <details className="agent-install__details agent-install__section">
          <summary><h3 id="agent-install-heading">{text.installTitle}</h3></summary>
          <p className="agent-install__install-lead">
            <DodTermText text={text.installLead} language={language} seen={new Set()} />
          </p>
          <h4>{text.tokenStep}</h4>
          <details className="agent-install__callout">
            <summary>{text.tokenWhyTitle}</summary>
            <ul>
              {text.tokenWhy.map((line) => (
                <li key={line}>
                  <DodTermText text={line} language={language} seen={new Set()} />
                </li>
              ))}
            </ul>
          </details>
          <div className="agent-install__start">
            <label className="agent-install__token-name">
              <span>{text.tokenLabel}</span>
              <input
                type="text"
                value={tokenLabel}
                disabled={busy}
                onChange={(event) => setTokenLabel(event.target.value)}
                placeholder={text.tokenLabelPlaceholder}
                aria-label={text.tokenLabel}
                aria-describedby="agent-token-name-hint"
                autoComplete="off"
              />
              <small id="agent-token-name-hint">{text.tokenLabelHint}</small>
            </label>
            <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => void createToken()} disabled={busy || !accessToken}>
              {busy ? text.creating : text.create}
            </button>
          </div>
          {token ? (
            <p className="agent-install__token" role="status">
              <code>{token}</code>
              <button
                type="button"
                className="dashboard-security-inline-btn"
                onClick={() => void copyText(token, language === 'es' ? 'Token copiado.' : 'Token copied.')}
              >
                {text.copyToken}
              </button>
              <span>{text.tokenHelp}</span>
            </p>
          ) : null}
          <h4 className="agent-install__subhead">{text.tokensTitle}</h4>
          <p className="agent-install__hint agent-install__token-prefix-hint">{text.tokenPrefixHint}</p>
          {revokedTokenCount > 0 ? (
            <label className="agent-install__hide-revoked">
              <input
                type="checkbox"
                checked={hideRevokedTokens}
                onChange={(event) => setHideRevokedTokens(event.target.checked)}
              />
              <span>
                {text.tokensHideRevoked}
                {hideRevokedTokens ? ` (${revokedTokenCount} ${text.tokensHiddenCount})` : ''}
              </span>
            </label>
          ) : null}
          {visibleTokens.length ? (
            <ul className="agent-install__tokens">
              {visibleTokens.map((item) => (
                <li key={item.token_id}>
                  <div className="agent-install__device-main">
                    <strong>{item.label || item.prefix || item.token_id}</strong>
                    <span>
                      {item.active ? text.active : text.revoked}
                      {item.prefix ? ` · ${text.tokenPrefixLabel} ${item.prefix}…` : ''}
                    </span>
                  </div>
                  {item.active ? (
                    <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void revokeToken(item.token_id)}>
                      {text.revoke}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="agent-install__hint">
              {tokens.length && hideRevokedTokens
                ? `${text.tokensHideRevoked} (${revokedTokenCount} ${text.tokensHiddenCount})`
                : text.tokensEmpty}
            </p>
          )}
        </details>

        <details className="agent-install__details agent-install__section">
          <summary><h3>{text.downloadStep}</h3></summary>
          <p className="visually-hidden">{text.downloadsTitle}</p>
          {downloadsMissing ? (
            <p className="agent-install__hint" role="status">{text.downloadMissing}</p>
          ) : null}
          <div className="agent-install__detect">
            <button
              type="button"
              className="dashboard-btn agent-install__detect-btn"
              disabled={busy || !accessToken}
              onClick={() => void detectMyDevice()}
            >
              {text.detectDevice}
            </button>
            {detectNote ? (
              <p className="agent-install__hint" role="status">{detectNote}</p>
            ) : null}
          </div>
          {showMacGatekeeper ? (
            <aside className="agent-install__callout" role="note">
              <strong>{text.macGatekeeperTitle}</strong>
              <p>
                <DodTermText text={text.macGatekeeperBody} language={language} seen={new Set()} />
              </p>
              <ol>
                {text.macGatekeeperSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <code>{text.macGatekeeperTerminal}</code>
            </aside>
          ) : null}
          <div className="agent-install__download-groups" ref={downloadListRef}>
            {downloadGroups.map((group) => (
              <div key={group.os} className="agent-install__download-group">
                <h5>{osGroupLabel(group.os, language)}</h5>
                <ul className="agent-install__download-list">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`dashboard-btn agent-install__download-btn${detectedDownloadId === item.id ? ' is-detected' : ''}`}
                        disabled={busy || item.available === false}
                        onClick={() => void downloadBinary(item)}
                        aria-current={detectedDownloadId === item.id ? 'true' : undefined}
                      >
                        <span>
                          {downloadLabel(item)}
                          {item.signed === false || item.os === 'darwin' ? (language === 'es' ? ' · sin firmar' : ' · unsigned') : ''}
                        </span>
                        <span className={`agent-install__ready ${item.available === false ? 'is-missing' : 'is-ready'}`}>
                          {item.available === false ? text.notReady : text.ready}
                        </span>
                      </button>
                      {item.note ? (
                        <p className="agent-install__hint">
                          <DodTermText text={item.note} language={language} seen={new Set()} />
                        </p>
                      ) : null}
                      {item.sha256 ? (
                        <span className="agent-install__hash">
                          <code title={item.sha256}>{item.sha256.slice(0, 16)}…</code>
                          <button type="button" className="dashboard-security-inline-btn" onClick={() => void copySha(item.sha256 || '')}>
                            {text.copyHash}
                          </button>
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="agent-install__downloads">
            <button
              type="button"
              className="dashboard-btn"
              disabled={busy || !accessToken}
              onClick={() => void downloadBinary({
                id: 'checksums.txt',
                os: 'any',
                arch: 'any',
                filename: 'checksums.txt',
                url: checksumsUrl || `${gateway}/api/v1/agent/download/checksums.txt`,
              })}
            >
              {text.checksumsDownload}
            </button>
          </div>
          <p className="agent-install__hint">{text.downloadChecksumNote}</p>
        </details>

        <details className="agent-install__details agent-install__section" open>
          <summary><h3>{text.runStep}</h3></summary>
          <p className="agent-install__trust" role="note">
            {language === 'es'
              ? 'Solo lectura · HTTPS · no cambia cargas ni abre su cuenta en la nube. El escaneo público del sitio ya funciona sin esto.'
              : 'Read-only · HTTPS · does not change workloads or open your cloud account. Public website scan already works without this.'}
          </p>
          <p className="visually-hidden">{text.stepsTitle}</p>
          <div className="agent-install__tabs" role="tablist" aria-label={text.stepsTitle}>
            {([
              { id: 'server' as const, label: text.tabServer },
              { id: 'windows' as const, label: text.tabWindows },
              { id: 'docker' as const, label: text.tabDocker },
              { id: 'kubernetes' as const, label: text.tabK8s },
              { id: 'cloud' as const, label: text.tabCloud },
            ]).map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={installTab === tab.id}
                className={installTab === tab.id ? 'is-active' : undefined}
                onClick={() => setInstallTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {installTab === 'cloud' ? (
            <div className="agent-install__tabs agent-install__aws-runtimes" role="tablist" aria-label={language === 'es' ? 'Nube / CLI' : 'Cloud / CLI'}>
              {([
                { id: 'oneclick' as const, label: language === 'es' ? 'AWS (un clic)' : 'AWS (one-click)' },
                { id: 'ssm' as const, label: language === 'es' ? 'AWS CLI (SSM)' : 'AWS CLI (SSM)' },
                { id: 'ec2' as const, label: 'EC2' },
                { id: 'ecs' as const, label: 'ECS' },
                { id: 'eks' as const, label: 'EKS' },
                { id: 'azure' as const, label: 'Azure CLI' },
                { id: 'gcp' as const, label: 'gcloud' },
              ]).map((runtime) => (
                <button
                  key={runtime.id}
                  type="button"
                  role="tab"
                  aria-selected={awsRuntime === runtime.id}
                  className={awsRuntime === runtime.id ? 'is-active' : undefined}
                  onClick={() => setAwsRuntime(runtime.id)}
                >
                  {runtime.label}
                </button>
              ))}
            </div>
          ) : null}
          {(installTab === 'kubernetes' || installTab === 'docker' || installTab === 'server' || installTab === 'cloud') ? (
            <div className="agent-install__guided">
              <div className="agent-install__guided-head">
                <strong>
                  {language === 'es' ? 'Instalación guiada — copie un comando' : 'Guided install — copy one command'}
                </strong>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  onClick={() => void copyText(
                    guidedCommandForTab(),
                    language === 'es' ? 'Comando copiado.' : 'Command copied.',
                  )}
                >
                  {language === 'es' ? 'Copiar comando' : 'Copy command'}
                </button>
              </div>
              <pre className="agent-install__guided-cmd" tabIndex={0}>
                {guidedCommandForTab()}
              </pre>
              {installTab === 'server' && guidedBrewLine ? (
                <p className="agent-install__hint">
                  {language === 'es'
                    ? 'También con Homebrew (macOS/Linux): '
                    : 'Also with Homebrew (macOS/Linux): '}
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => void copyText(guidedBrewLine, language === 'es' ? 'Comando copiado.' : 'Command copied.')}
                  >
                    {language === 'es' ? 'Copiar brew' : 'Copy brew'}
                  </button>
                </p>
              ) : null}
              {!token ? (
                <p className="agent-install__hint">
                  {language === 'es'
                    ? 'Cree un token arriba para rellenar ncagent_… automáticamente.'
                    : 'Create a token above to fill in ncagent_… automatically.'}
                </p>
              ) : null}
              {token && devices.length === 0 ? (
                <p className="agent-install__waiting" role="status">
                  {language === 'es'
                    ? 'Esperando el primer check-in… vuelva a esta página en un minuto.'
                    : 'Waiting for the first check-in… refresh this page in a minute.'}
                </p>
              ) : null}
            </div>
          ) : null}
          <ol className="agent-install__steps">
            {stepList.map((step) => (
              <li key={`${installTab}-${step}`}>
                {step.includes('nextcompli-agent') || step.includes('docker ') || step.includes('helm ') || step.includes('args:') || step.includes('$env:') || step.includes('msiexec') || step.includes('irm ') || step.includes('chmod ') || step.includes('xattr ') || step.includes('--set ') ? (
                  <code>{step}</code>
                ) : (
                  step
                )}
              </li>
            ))}
          </ol>
          <h4 className="agent-install__subhead">{text.multiTitle}</h4>
          <ol className="agent-install__steps">
            {multiSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </details>

        <details className="agent-install__details agent-install__section">
          <summary><h3>{text.securityTitle}</h3></summary>
          {(instructions.security || []).length ? (
            <ol className="agent-install__steps">
              {(instructions.security || []).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : (
            <p className="agent-install__hint">
              <DodTermText
                text={language === 'es'
                  ? 'Cada dispositivo usa el token de esta suscripción por HTTPS. NextCompli guarda solo el hash. El checksum solo verifica el archivo descargado.'
                  : 'Each device uses this subscription’s install token over HTTPS. NextCompli stores only the hash. The checksum only verifies the downloaded file.'}
                language={language}
                seen={new Set()}
              />
            </p>
          )}
        </details>

        {(() => {
          const readyFleet = fleetPackages.filter((item) => item.available !== false);
          if (!readyFleet.length) {
            return null;
          }
          return (
            <details className="agent-install__details agent-install__section">
              <summary><h3>{text.fleetTitle}</h3></summary>
              <p className="agent-install__hint">
                <DodTermText text={text.fleetLead} language={language} seen={new Set()} />
              </p>
              <ul className="agent-install__download-list">
                {readyFleet.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={busy}
                      onClick={() => void downloadBinary(item)}
                    >
                      {downloadLabel(item)}
                      {item.signed === false ? (language === 'es' ? ' · sin firmar' : ' · unsigned') : ''}
                    </button>
                    {item.note ? (
                      <span className="agent-install__hint">
                        <DodTermText text={item.note} language={language} seen={new Set()} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          );
        })()}

        <details className="agent-install__details agent-install__section">
          <summary><h3>{text.packageManagersTitle}</h3></summary>
          <div className="agent-install__pkg-grid">
            {([
              { key: 'homebrew', title: text.packageHomebrew, channel: packageManagers.homebrew },
              { key: 'winget', title: text.packageWinget, channel: packageManagers.winget },
            ] as const).map((row) => {
              const ready = row.channel?.status === 'ready';
              const steps = Array.isArray(row.channel?.install) ? row.channel.install : [];
              const primary = steps.find((step) => step.includes('brew install') || step.includes('winget install')) || steps[0] || '';
              return (
                <article key={row.key} className={`agent-install__pkg-card${ready ? ' is-ready' : ''}`}>
                  <header>
                    <strong>{row.title}</strong>
                    <span className={`agent-install__ready${ready ? '' : ' is-missing'}`}>
                      {ready ? text.packageReady : text.packageUnavailable}
                    </span>
                  </header>
                  {steps.length ? (
                    <ol className="agent-install__steps">
                      {steps.map((step) => (
                        <li key={step}>
                          <code>{step}</code>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {ready && primary ? (
                    <button
                      type="button"
                      className="dashboard-btn"
                      onClick={() => void copyText(primary, language === 'es' ? 'Comando copiado.' : 'Command copied.')}
                    >
                      {text.packageCopy}
                    </button>
                  ) : null}
                  {ready && row.channel?.manifest_zip_url ? (
                    <a className="dashboard-security-inline-btn" href={row.channel.manifest_zip_url}>
                      manifests.zip
                    </a>
                  ) : null}
                  {ready && row.channel?.formula_url ? (
                    <a className="dashboard-security-inline-btn" href={row.channel.formula_url}>
                      nextcompli-agent.rb
                    </a>
                  ) : null}
                </article>
              );
            })}
          </div>
        </details>
      </div>
      ) : null}

      {agentView === 'scope' ? (
        <div className="agent-install__view-panel agent-install__scope" role="tabpanel" aria-label={text.viewScope}>
          <h3 className="agent-install__scope-title">{text.scopeTitle}</h3>
          <p className="agent-install__hint">
            <DodTermText text={text.scopeLead} language={language} seen={new Set()} />
          </p>

          <article className="agent-install__scope-card agent-install__schedule-card">
            <header>
              <strong>{text.scheduleTitle}</strong>
            </header>
            <p className="agent-install__hint" style={{ marginTop: 0 }}>
              <DodTermText text={text.scheduleLead} language={language} seen={new Set()} />
            </p>
            <div className="agent-install__scope-item">
              <div>
                <strong>{text.scheduleCheckin}</strong>
              </div>
              <label className="agent-install__scope-select">
                <span className="visually-hidden">{text.scheduleCheckin}</span>
                <select
                  value={checkinCadenceId}
                  disabled={busy || !scopeLoaded}
                  onChange={(event) => setCheckinCadenceId(event.target.value as CheckinCadenceId)}
                >
                  {(checkinCadence?.presets?.checkin || [
                    { id: 'manual', label_en: 'Manual only', label_es: 'Solo manual' },
                    { id: 'every_4h', label_en: 'Every 4 hours', label_es: 'Cada 4 horas' },
                    { id: 'daily', label_en: 'Daily (recommended)', label_es: 'Diario (recomendado)' },
                    { id: 'weekly', label_en: 'Weekly', label_es: 'Semanal' },
                  ]).map((option) => (
                    <option key={option.id} value={option.id}>
                      {language === 'es' ? (option.label_es || option.label_en) : (option.label_en || option.id)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="agent-install__scope-item">
              <div>
                <strong>
                  <DodTermText text={text.scheduleDiscovery} language={language} seen={new Set()} />
                </strong>
              </div>
              <label className="agent-install__scope-select">
                <span className="visually-hidden">{text.scheduleDiscovery}</span>
                <select
                  value={discoveryCadenceId}
                  disabled={busy || !scopeLoaded}
                  onChange={(event) => setDiscoveryCadenceId(event.target.value as DiscoveryCadenceId)}
                >
                  {(checkinCadence?.presets?.discovery || [
                    { id: 'manual', label_en: 'Manual only', label_es: 'Solo manual' },
                    { id: 'with_checkin', label_en: 'With each check-in', label_es: 'Con cada check-in' },
                    { id: 'daily', label_en: 'Daily', label_es: 'Diario' },
                    { id: 'weekly', label_en: 'Weekly', label_es: 'Semanal' },
                  ]).map((option) => (
                    <option key={option.id} value={option.id}>
                      {language === 'es' ? (option.label_es || option.label_en) : (option.label_en || option.id)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {checkinCadence?.checkin?.cron || checkinCadenceId !== 'manual' ? (
              <p className="agent-install__finding-meta">
                {text.scheduleCron}:{' '}
                <code>
                  {(checkinCadence?.presets?.checkin || []).find((row) => row.id === checkinCadenceId)?.cron
                    || checkinCadence?.checkin?.cron
                    || (checkinCadenceId === 'every_4h' ? '0 */4 * * *' : checkinCadenceId === 'weekly' ? '30 3 * * 0' : checkinCadenceId === 'daily' ? '15 2 * * *' : '—')}
                </code>
              </p>
            ) : null}
            <p className="agent-install__finding-meta">
              {text.scheduleDelivery}:{' '}
              {language === 'es'
                ? (checkinCadence?.delivery?.note_es || 'El agente obtiene el horario en el check-in (no hay push).')
                : (checkinCadence?.delivery?.note_en || 'The agent pulls the schedule on check-in (no push).')}
            </p>
          </article>

          <div className="agent-install__scope-grid">
            <article className="agent-install__scope-card is-always">
              <header>
                <strong>{text.scopeAlwaysTitle}</strong>
                <span className="agent-install__scope-chip">{language === 'es' ? 'Siempre' : 'Always'}</span>
              </header>
              <p>{text.scopeAlwaysBody}</p>
            </article>
            <article className="agent-install__scope-card">
              <header>
                <strong>{text.scopeOptionalTitle}</strong>
              </header>
              <div className="agent-install__scope-item">
                <div>
                  <strong>{text.scopeHintsTitle}</strong>
                  <p>{text.scopeHintsBody}</p>
                </div>
                <span className="agent-install__scope-chip is-on">{language === 'es' ? 'Con check-in' : 'With check-in'}</span>
              </div>
              <div className="agent-install__scope-item">
                <div>
                  <strong>
                    <DodTermText text={text.scopeDiscoveryTitle} language={language} seen={new Set()} />
                  </strong>
                  <p>
                    <DodTermText text={text.scopeDiscoveryBody} language={language} seen={new Set()} />
                  </p>
                </div>
                <label className="agent-install__scope-select">
                  <span className="visually-hidden">{text.scopeDiscoveryTitle}</span>
                  <select
                    value={discoveryMode}
                    disabled={busy || !scopeLoaded}
                    onChange={(event) => setDiscoveryMode(event.target.value as ScopePermissionMode)}
                  >
                    <option value="off">{text.scopeModeOff}</option>
                    <option value="on_approval">{text.scopeModeApproval}</option>
                  </select>
                </label>
              </div>
              {discoveryMode !== 'off' ? (
                <div className="agent-install__scan-roots">
                  <strong>
                    <DodTermText text={text.scanRootsTitle} language={language} seen={new Set()} />
                  </strong>
                  <p className="agent-install__hint">
                    <DodTermText text={text.scanRootsLead} language={language} seen={new Set()} />
                  </p>
                  <div className="agent-install__scan-presets" role="group" aria-label={text.scanRootsPresets}>
                    {(scanRootPresets.length ? scanRootPresets : [
                      { id: 'documents', path: '~/Documents', os: 'any', label_en: 'User Documents', label_es: 'Documentos del usuario' },
                      { id: 'desktop', path: '~/Desktop', os: 'any', label_en: 'Desktop', label_es: 'Escritorio' },
                      { id: 'downloads', path: '~/Downloads', os: 'any', label_en: 'Downloads', label_es: 'Descargas' },
                      { id: 'win_downloads', path: '%USERPROFILE%\\Downloads', os: 'windows', label_en: 'Windows Downloads', label_es: 'Descargas (Windows)' },
                      { id: 'mac_documents', path: '/Users/*/Documents', os: 'macos', label_en: 'macOS Documents (all users)', label_es: 'Documentos macOS (todos)' },
                      { id: 'linux_documents', path: '/home/*/Documents', os: 'linux', label_en: 'Linux Documents (all users)', label_es: 'Documentos Linux (todos)' },
                    ]).map((preset) => (
                      <button
                        key={preset.id || preset.path}
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={busy}
                        onClick={() => addScanRoot({
                          id: preset.id,
                          path: preset.path,
                          os: (preset.os || 'any') as ScanRootOs,
                          recursive: true,
                          preset: true,
                        })}
                      >
                        {language === 'es' ? (preset.label_es || preset.label_en || preset.path) : (preset.label_en || preset.path)}
                      </button>
                    ))}
                  </div>
                  <ul className="agent-install__scan-root-list">
                    {scanRoots.length ? scanRoots.map((root, index) => (
                      <li key={`${root.path}:${root.os}:${index}`}>
                        <code>{root.path}</code>
                        <span>
                          {(root.os || 'any') === 'any' ? text.scanRootsOsAny : String(root.os)}
                          {' · '}
                          {root.recursive === false ? text.scanRootsThisFolder : text.scanRootsRecursive}
                        </span>
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          disabled={busy}
                          onClick={() => removeScanRoot(index)}
                        >
                          {text.scanRootsRemove}
                        </button>
                      </li>
                    )) : (
                      <li className="agent-install__hint">{text.scanRootsEmpty}</li>
                    )}
                  </ul>
                  <div className="agent-install__scan-custom">
                    <label>
                      <span>{text.scanRootsCustom}</span>
                      <input
                        type="text"
                        value={customRootPath}
                        onChange={(event) => setCustomRootPath(event.target.value)}
                        placeholder={language === 'es' ? 'D:\\Contratos' : 'D:\\Contracts'}
                        autoComplete="off"
                        disabled={busy}
                      />
                    </label>
                    <label>
                      <span>{text.scanRootsOs}</span>
                      <select
                        value={customRootOs}
                        disabled={busy}
                        onChange={(event) => setCustomRootOs(event.target.value as ScanRootOs)}
                      >
                        <option value="any">{text.scanRootsOsAny}</option>
                        <option value="windows">Windows</option>
                        <option value="macos">macOS</option>
                        <option value="linux">Linux</option>
                      </select>
                    </label>
                    <label className="agent-install__scan-recursive">
                      <input
                        type="checkbox"
                        checked={customRootRecursive}
                        disabled={busy}
                        onChange={(event) => setCustomRootRecursive(event.target.checked)}
                      />
                      <span>{customRootRecursive ? text.scanRootsRecursive : text.scanRootsThisFolder}</span>
                    </label>
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={busy || !customRootPath.trim()}
                      onClick={() => {
                        addScanRoot({
                          path: customRootPath.trim(),
                          os: customRootOs,
                          recursive: customRootRecursive,
                        });
                        setCustomRootPath('');
                      }}
                    >
                      {text.scanRootsAdd}
                    </button>
                  </div>
                  <p className="agent-install__hint">{text.scanRootsCustomHint}</p>
                  <div className="agent-install__scan-preview">
                    <strong>{text.scanRootsPreview}</strong>
                    <label>
                      <span>{text.scanRootsPreviewSample}</span>
                      <input
                        type="text"
                        value={previewSample}
                        onChange={(event) => setPreviewSample(event.target.value)}
                        placeholder={language === 'es' ? '/Users/ana/Documents/vendor-list.csv' : '/Users/ana/Documents/vendor-list.csv'}
                        autoComplete="off"
                        disabled={busy}
                      />
                    </label>
                    <label>
                      <span>{text.scanRootsPreviewHost}</span>
                      <select
                        value={previewHostOs}
                        disabled={busy}
                        onChange={(event) => setPreviewHostOs(event.target.value as ScanRootOs)}
                      >
                        <option value="macos">macOS</option>
                        <option value="windows">Windows</option>
                        <option value="linux">Linux</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      disabled={busy || !accessToken}
                      onClick={() => void runScanRootPreview()}
                    >
                      {text.scanRootsPreviewRun}
                    </button>
                    {previewResult ? <p className="agent-install__finding-meta" role="status">{previewResult}</p> : null}
                  </div>
                </div>
              ) : null}
            </article>
            <article className="agent-install__scope-card is-never">
              <header>
                <strong>{text.scopeNeverTitle}</strong>
                <span className="agent-install__scope-chip is-never">{language === 'es' ? 'Bloqueado' : 'Locked'}</span>
              </header>
              <p>{text.scopeNeverBody}</p>
            </article>
          </div>
          <div className="agent-install__actions">
            <button
              type="button"
              className="dashboard-btn dashboard-btn-primary"
              disabled={busy || !accessToken || !scopeLoaded}
              onClick={() => void saveScope()}
            >
              {busy ? text.scopeSaving : text.scopeSave}
            </button>
            <button
              type="button"
              className="dashboard-btn"
              disabled={busy || !accessToken || !scopeLoaded
                || (checkinCadenceId === 'daily' && discoveryCadenceId === 'with_checkin')}
              onClick={() => void resetScheduleDefaults()}
            >
              {text.scheduleDefaults}
            </button>
          </div>
        </div>
      ) : null}

      {agentView === 'connect2ai' ? (
        <div className="agent-install__view-panel" role="tabpanel" aria-label={text.viewConnect2Ai}>
          <Connect2AiPanel
            language={language}
            gatewayBaseUrl={gateway}
            accessToken={accessToken}
          />
        </div>
      ) : null}

      {error ? <p className="agent-install__alert" role="alert">{error}</p> : null}
      {message ? <p className="agent-install__notice" role="status">{message}</p> : null}
    </section>
  );
}
